import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, Heart, Moon, Sparkles, Sun, ArrowRight, Mail, Download } from "lucide-react";

const WomensHormoneGuide = () => {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-700 to-rose-600 text-white py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-4">
            <Mail className="w-4 h-4" />
            <span className="text-sm font-medium">Guide Sent to Your Email</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Your Free Hormone Reset Guide
          </h1>
          <p className="text-pink-100 text-lg">
            Check your inbox — your complete guide is on its way. Here's a preview of what's inside.
          </p>
        </div>
      </div>

      {/* Guide Content Preview */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
              <Download className="w-6 h-6 text-pink-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Hormone Reset Guide</h2>
              <p className="text-gray-500 text-sm">For Women Experiencing Early Signs of Imbalance</p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <h3 className="text-xl font-bold text-gray-900 mb-4">What's Inside:</h3>
            
            <div className="space-y-6">
              {/* Section 1 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-pink-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                  Understanding Hormonal Shifts
                </h4>
                <p className="text-gray-600">
                  After 30, your hormonal landscape begins to shift. Estrogen and progesterone fluctuate differently, 
                  cortisol patterns change, and thyroid function can be affected. These shifts impact mood, energy, 
                  sleep, and how you feel in your own body. Understanding this is empowering — it's not "all in your head."
                </p>
              </div>

              {/* Section 2 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-pink-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  The 4 Pillars of Hormonal Balance
                </h4>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-pink-500 mt-1 flex-shrink-0" />
                    <span><strong>Mood Stability:</strong> Supporting neurotransmitter balance and stress response to reduce emotional volatility.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Sun className="w-5 h-5 text-yellow-500 mt-1 flex-shrink-0" />
                    <span><strong>Energy Rhythm:</strong> Aligning your energy with natural circadian patterns for consistent vitality.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Heart className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" />
                    <span><strong>Cycle Harmony:</strong> Supporting regular, comfortable cycles through hormone-balancing botanicals.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Moon className="w-5 h-5 text-purple-500 mt-1 flex-shrink-0" />
                    <span><strong>Restorative Sleep:</strong> Deep sleep is when hormones reset. Protecting this is essential.</span>
                  </li>
                </ul>
              </div>

              {/* Section 3 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-pink-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  Daily Habits for Hormonal Health
                </h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Protein with every meal (stabilizes blood sugar)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Morning movement (even 10 minutes helps cortisol rhythm)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Seed cycling (flax/pumpkin days 1-14, sesame/sunflower days 15-28)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Evening wind-down ritual (30 min before bed, no screens)</span>
                  </li>
                </ul>
              </div>

              {/* Section 4 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-pink-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
                  Key Herbs for Women's Hormonal Balance
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-gray-600">
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Vitex (Chasteberry)</p>
                    <p className="text-sm">Supports healthy progesterone levels</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Maca Root</p>
                    <p className="text-sm">Balances hormones and boosts energy</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Ashwagandha</p>
                    <p className="text-sm">Reduces cortisol, supports thyroid</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Dong Quai</p>
                    <p className="text-sm">Traditional female tonic herb</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-pink-600 to-rose-600 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Ready for Comprehensive Support?</h3>
          <p className="text-pink-100 mb-6 max-w-lg mx-auto">
            The Balance Protocol combines these herbs in research-backed dosages, specifically formulated for women experiencing what you described.
          </p>
          <Button
            onClick={() => navigate("/womens-balance-protocol")}
            className="bg-white text-pink-700 hover:bg-pink-50 px-8 py-3 text-lg font-semibold rounded-full"
          >
            Explore the Balance Protocol <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>

        {/* Back Links */}
        <div className="text-center mt-8 space-x-4">
          <Link to="/womens-assessment" className="text-pink-600 hover:text-pink-700 font-medium">
            ← Retake Assessment
          </Link>
          <Link to="/" className="text-gray-500 hover:text-gray-700 font-medium">
            Return Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WomensHormoneGuide;
